import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Fade,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import { formatMoney } from "../../../../../shared/utils/formatMoney";
import {
  formatProductionDate,
  formatProductionQuantity,
  formatProductionYield,
} from "../utils/productionFormatters";
import {
  PRODUCTION_CALCULATION_MODE_LABELS,
  PRODUCTION_COMPONENT_TYPE_LABELS,
  PRODUCTION_STATUS,
  PRODUCTION_STATUS_LABELS,
} from "../utils/productionModel";

const detailGrid =
  "minmax(230px, 1.8fr) minmax(115px, .85fr) minmax(125px, .9fr) minmax(160px, 1fr)";

function StatusBadge({ status }) {
  const produced = status === PRODUCTION_STATUS.PRODUCED;
  const cancelled = status === PRODUCTION_STATUS.CANCELLED;
  return (
    <Box
      sx={{
        flexShrink: 0,
        px: 1.4,
        py: 0.55,
        borderRadius: "20px",
        bgcolor: produced
          ? "rgba(33, 150, 83, 0.16)"
          : cancelled
          ? "rgba(195, 60, 60, 0.16)"
          : "neutral.150",
        boxShadow: "inset 0px 0px 3px rgba(117, 117, 138, 0.2)",
      }}
    >
      <Typography
        sx={{
          color: produced
            ? "rgba(18, 111, 63, 1)"
            : cancelled
            ? "rgba(160, 49, 49, 1)"
            : "neutral.800",
          fontSize: 12,
          fontWeight: 850,
        }}
      >
        {PRODUCTION_STATUS_LABELS[status] || status || "—"}
      </Typography>
    </Box>
  );
}

function SummaryIndicator({ label, value }) {
  return (
    <Box
      sx={{
        minWidth: 0,
        px: { xs: 1.5, sm: 2 },
        py: 1.15,
        borderRadius: "14px",
        bgcolor: "neutral.600",
        boxShadow: "inset 0px 0px 3px rgba(33, 33, 39, 0.22)",
      }}
    >
      <Typography sx={{ color: "neutral.100", fontSize: 11, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.2,
          color: "logoblanco",
          fontSize: { xs: 16, sm: 18 },
          fontWeight: 800,
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function MetadataItem({ label, value }) {
  return (
    <Typography sx={{ color: "primary.900", fontSize: 13, lineHeight: 1.45 }}>
      <Box component="span" sx={{ fontWeight: 800 }}>
        {label}: {" "}
      </Box>
      <Box component="span" sx={{ fontWeight: 600 }}>
        {value}
      </Box>
    </Typography>
  );
}

function ComponentTypeBadge({ type }) {
  const ingredient = type === "INGREDIENT";
  return (
    <Box
      sx={{
        display: "inline-flex",
        justifyContent: "center",
        minWidth: 78,
        px: 1,
        py: 0.45,
        borderRadius: "16px",
        bgcolor: ingredient ? "areacocina.200" : "secondary.150",
      }}
    >
      <Typography sx={{ color: "primary.900", fontSize: 11, fontWeight: 800 }}>
        {PRODUCTION_COMPONENT_TYPE_LABELS[type] || type || "—"}
      </Typography>
    </Box>
  );
}

function ProductionDetailContent({ detail }) {
  const lines = Array.isArray(detail.lines) ? detail.lines : [];
  const cancellationMetadata = [
    detail.cancelledBy ? ["Anulada por", detail.cancelledBy] : null,
    detail.cancelledAt
      ? ["Fecha de anulación", formatProductionDate(detail.cancelledAt)]
      : null,
  ].filter(Boolean);

  return (
    <Stack spacing={1.5}>
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: "22px",
          bgcolor: "neutral.50",
          boxShadow: "inset 0px 0px 3px rgba(117, 117, 138, 0.28)",
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignItems: "flex-start", justifyContent: "space-between" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h2"
              sx={{
                color: "primary.900",
                fontSize: { xs: 18, sm: 21 },
                fontWeight: 850,
                lineHeight: 1.2,
                overflowWrap: "anywhere",
              }}
            >
              {detail.preparedProduct?.name || "Elaborado"}
            </Typography>
            <Typography
              sx={{ mt: 0.5, color: "neutral.700", fontSize: 13, fontWeight: 700 }}
            >
              {formatProductionDate(detail.producedAt)}
            </Typography>
          </Box>
          <StatusBadge status={detail.status} />
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 1.25,
            mt: 1.7,
          }}
        >
          <SummaryIndicator
            label="Cantidad producida"
            value={formatProductionQuantity(
              detail.producedQuantity,
              detail.preparedProduct?.resultUnit
            )}
          />
          <SummaryIndicator label="Costo total" value={formatMoney(detail.totalCost)} />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 0.35,
            mt: 1.7,
          }}
        >
          <MetadataItem label="Producido por" value={detail.producedBy || "—"} />
          <MetadataItem
            label="Forma de cálculo"
            value={
              PRODUCTION_CALCULATION_MODE_LABELS[detail.calculationMode] ||
              detail.calculationMode ||
              "—"
            }
          />
          {cancellationMetadata.map(([label, value]) => (
            <MetadataItem key={label} label={label} value={value} />
          ))}
        </Box>
      </Box>

      <Typography
        component="h3"
        sx={{ px: 0.5, color: "primary.900", fontSize: 15, fontWeight: 850 }}
      >
        Componentes utilizados
      </Typography>

      <Box
        sx={{
          maxHeight: "min(38vh, 360px)",
          borderRadius: "18px",
          overflow: "auto",
          bgcolor: "neutral.50",
          boxShadow: "inset 0px 0px 2px rgba(117, 117, 138, 0.2)",
        }}
      >
        <Box sx={{ minWidth: 700 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: detailGrid,
              gap: 1,
              px: 2,
              py: 1.25,
              bgcolor: "neutral.600",
              position: "sticky",
              top: 0,
              zIndex: 1,
            }}
          >
            {["Componente", "Tipo", "Rendimiento", "Cantidad utilizada"].map(
              (label) => (
                <Typography
                  key={label}
                  sx={{
                    color: "logoblanco",
                    fontSize: 12,
                    fontWeight: 750,
                    textAlign: label === "Componente" ? "left" : "right",
                  }}
                >
                  {label}
                </Typography>
              )
            )}
          </Box>
          {lines.length ? (
            lines.map((line) => (
              <Box
                key={
                  line.id ||
                  `${line.componentType}-${line.componentId}-${line.lineOrder}`
                }
                sx={{
                  display: "grid",
                  gridTemplateColumns: detailGrid,
                  gap: 1,
                  px: 2,
                  py: 1.25,
                  borderBottom: "1px solid rgba(0,0,0,.06)",
                  alignItems: "center",
                  "& > *": { minWidth: 0 },
                }}
              >
                <Typography
                  title={line.name || undefined}
                  sx={{
                    fontSize: 13,
                    fontWeight: 750,
                    color: "primary.900",
                    lineHeight: 1.3,
                    overflowWrap: "anywhere",
                  }}
                >
                  {line.name || "—"}
                </Typography>
                <Box sx={{ textAlign: "right" }}>
                  <ComponentTypeBadge type={line.componentType} />
                </Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "primary.900",
                    textAlign: "right",
                  }}
                >
                  {formatProductionYield(line.yieldFactor)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "primary.900",
                    textAlign: "right",
                  }}
                >
                  {formatProductionQuantity(line.requiredQuantity, line.unit)}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography
              sx={{ p: 2, color: "neutral.700", fontSize: 13, fontWeight: 600 }}
            >
              La producción no contiene componentes directos.
            </Typography>
          )}
        </Box>
      </Box>
    </Stack>
  );
}

export default function ProductionDetailDialog({
  open,
  detail,
  loading,
  error,
  onRetry,
  onClose,
}) {
  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      maxWidth={false}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      PaperProps={{
        sx: {
          bgcolor: "transparent",
          boxShadow: "none",
          borderRadius: "30px",
          overflow: "visible",
          m: 1.5,
        },
      }}
      slotProps={{
        backdrop: {
          timeout: 180,
          sx: { bgcolor: "rgba(2, 8, 23, 0.45)", backdropFilter: "blur(3px)" },
        },
      }}
    >
      <Fade in={Boolean(open)} timeout={180}>
        <Box
          sx={{
            width: "min(96vw, 920px)",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            bgcolor: "neutral.300",
            borderRadius: "30px",
            p: { xs: 2, sm: 2.5 },
            boxShadow: "0px 20px 60px rgba(0,0,0,.28)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "32px minmax(0, 1fr) 32px",
              gap: 1,
              alignItems: "center",
              mb: 1.6,
            }}
          >
            <Box />
            <Box
              sx={{
                maxWidth: 360,
                width: "100%",
                mx: "auto",
                px: 2,
                py: 0.8,
                bgcolor: "secondary.200",
                borderRadius: "16px",
              }}
            >
              <Typography sx={{ textAlign: "center", fontSize: 17, fontWeight: 800, color: "primary.900" }}>
                Detalle de producción
              </Typography>
            </Box>
            <IconButton
              type="button"
              size="small"
              onClick={onClose}
              aria-label="Cerrar detalle de producción"
            >
              <CloseIcon sx={{ color: "primary.900" }} />
            </IconButton>
          </Box>

          <Box sx={{ minHeight: 240, overflow: "auto" }}>
            {error ? (
              <Stack spacing={1.5} sx={{ minHeight: 240, justifyContent: "center" }}>
                <Alert severity="error">{error}</Alert>
                <Button
                  type="button"
                  variant="contained"
                  onClick={onRetry}
                  sx={{ alignSelf: "flex-end", borderRadius: "20px", textTransform: "none", fontWeight: 800 }}
                >
                  Reintentar
                </Button>
              </Stack>
            ) : loading || !detail ? (
              <Stack spacing={1} sx={{ minHeight: 240, alignItems: "center", justifyContent: "center" }}>
                <CircularProgress size={28} />
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                  Cargando detalle...
                </Typography>
              </Stack>
            ) : (
              <ProductionDetailContent detail={detail} />
            )}
          </Box>
        </Box>
      </Fade>
    </Dialog>
  );
}
